import React, { useEffect, useState } from "react";
import { t } from "@/lib/i18n";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Filter as FilterIcon, X, ChevronDown, Calendar, Link2, Lock, Search, Hash, Settings2, Check, Target } from "lucide-react";
import { getFormatDefaults } from "@/lib/chartTheme";

export { DATE_PRESETS } from "@/lib/datePresets";
import { DATE_PRESETS, resolvePreset, todayIn } from "@/lib/datePresets";

// "Today" for the effective regional timezone (tenant/user) — presets roll over at local midnight.
export function regionalToday(timezone = getFormatDefaults().timezone) { return todayIn(timezone); }

// Resolve a relative preset code to a concrete [from, to] ISO date pair (regional timezone + week start).
export function presetRange(code, fiscalStart = 1, opts = {}) {
  const d = getFormatDefaults();
  return resolvePreset(code, { fiscalStart, timezone: opts.timezone ?? d.timezone, firstDay: opts.firstDay ?? d.firstDay });
}

// Convert filter definitions + selected values into backend filter objects.
export function toActiveFilters(filtersDef = [], values = {}) {
  const out = [];
  for (const f of filtersDef) {
    const before = out.length;
    const v = values[f.id];
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    if (f.type === "multiselect") out.push({ dataset_id: f.dataset_id, field: f.field, op: "in", value: v });
    // Item 98 — FilterPanel types -------------------------------------------------
    else if (f.type === "checkbox") {
      const arr = Array.isArray(v) ? v : Array.isArray(v.v) ? v.v : [];
      if (!arr.length) continue;
      const exclude = !Array.isArray(v) && !!v.exclude;
      out.push({ dataset_id: f.dataset_id, field: f.field, op: exclude ? "not_in" : "in", value: arr });
    } else if (f.type === "search") {
      const term = String(v || "").trim();
      const fields = (f.fields || []).filter(Boolean);
      if (!term || !fields.length) continue;
      out.push({ dataset_id: f.dataset_id, field: fields[0], op: "search_any", value: term, fields });
    } else if (f.type === "toggle") {
      if (!v) continue;
      out.push({ dataset_id: f.dataset_id, field: f.field, op: f.op || "eq", value: f.value ?? true });
    } else if (f.type === "tier") {
      const lv = (f.levels || []).find((l) => String(l.key) === String(v));
      if (!lv) continue;
      out.push({ dataset_id: f.dataset_id, field: f.field, op: lv.op || "gte", value: lv.value });
    } else if (f.type === "numrange") {
      let a = null, b = null;
      if (Array.isArray(v)) { [a, b] = v; } else if (v && typeof v === "object" && v.preset) {
        const r = (f.ranges || []).find((x) => String(x.key) === String(v.preset));
        if (!r) continue;
        a = r.min ?? null; b = r.max ?? null;
      } else continue;
      const hasA = a != null && a !== "", hasB = b != null && b !== "";
      if (hasA && hasB) out.push({ dataset_id: f.dataset_id, field: f.field, op: "between", value: [Number(a), Number(b)] });
      else if (hasA) out.push({ dataset_id: f.dataset_id, field: f.field, op: "gte", value: Number(a) });
      else if (hasB) out.push({ dataset_id: f.dataset_id, field: f.field, op: "lte", value: Number(b) });
    } else if (f.type === "daterange") {
      if (v && typeof v === "object" && !Array.isArray(v) && v.preset === "last_n") {
        const n = Number(v.n) || 0;
        if (n > 0) {
          const iso = (d) => d.toISOString().slice(0, 10);
          const today = regionalToday();
          out.push({ dataset_id: f.dataset_id, field: f.field, op: "between", value: [iso(new Date(today - n * 86400000)), iso(today)] });
        }
      } else if (typeof v === "string" && v !== "custom") {
        const r = presetRange(v, f.fiscal_start);
        if (r) out.push({ dataset_id: f.dataset_id, field: f.field, op: "between", value: r });
      } else if (Array.isArray(v) && v[0] && v[1]) {
        out.push({ dataset_id: f.dataset_id, field: f.field, op: "between", value: v });
      }
    } else out.push({ dataset_id: f.dataset_id, field: f.field, op: "eq", value: v });
    // Item 195.9: tag filters with their widget scope so the query layer can honor it.
    if (out.length > before && f.scope && f.scope.mode && f.scope.mode !== "all") {
      out[out.length - 1].__scope = { mode: f.scope.mode, ids: f.scope.widget_ids || [] };
    }
    // Cross-source SQL widgets resolve {{where:<filter_id>:…}} tokens, so carry the id along.
    for (let i = before; i < out.length; i++) out[i].filter_id = f.id;
  }
  return out;
}

// Initial values map from per-filter defaults (author-set), used by Builder & Viewer on load.
export function defaultFilterValues(filtersDef = []) {
  const out = {};
  for (const f of filtersDef) {
    const d = f.default;
    if (d == null || d === "") continue;
    if (f.type === "multiselect" || f.type === "checkbox") out[f.id] = Array.isArray(d) ? d : String(d).split(",").map((s) => s.trim()).filter(Boolean);
    else if (f.type === "toggle") out[f.id] = d === true || d === "true";
    else if (f.type === "numrange") {
      const [a, b] = String(d).split(",").map((s) => s.trim());
      out[f.id] = [a === "" || a == null ? null : Number(a), b === "" || b == null ? null : Number(b)];
    } else out[f.id] = d;
  }
  return out;
}

// Active backend filter for a single definition (or null).
function activeFor(f, values) {
  return toActiveFilters([f], values)[0] || null;
}

// Walk parent_id ancestors of a filter definition (linked filter chains).
function ancestors(f, defById) {
  const out = [];
  let cur = f, guard = 0;
  while (cur?.parent_id && defById[cur.parent_id] && guard++ < 10) {
    cur = defById[cur.parent_id];
    out.push(cur);
  }
  return out;
}

// Given a changed filter id, clear values of all its descendants (linked chains).
export function clearDescendants(filtersDef, values, changedId) {
  const next = { ...values };
  const clear = (pid) => {
    for (const f of filtersDef) {
      if (f.parent_id === pid && next[f.id] != null) { delete next[f.id]; clear(f.id); }
    }
  };
  clear(changedId);
  return next;
}

async function fetchDistinct({ datasetId, field, filters, publicToken }) {
  if (publicToken) {
    const { data } = await api.post("/public/distinct", { token: publicToken, dataset_id: datasetId, field, filters });
    return data.values || [];
  }
  const { data } = await api.post(`/datasets/${datasetId}/distinct`, { field, filters });
  return data.values || [];
}

export function FilterBar({ filtersDef = [], values = {}, onChange, publicToken = null, editable = false, onRemove, onClearAll, vertical = false, settings = {}, onSettingsChange, onApplyAll, widgets = [], onUpdateDef }) {
  const applyMode = settings.apply_mode === "manual" ? "manual" : "instant";
  const [draft, setDraft] = useState(values);
  const valuesKey = JSON.stringify(values);
  useEffect(() => { setDraft(values); }, [valuesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleDefs = filtersDef.filter((f) => editable || !f.hidden);
  if (!visibleDefs.length) return null;

  const effValues = applyMode === "manual" ? draft : values;
  const handleChange = (fid, v) => {
    if (applyMode === "manual") setDraft((s) => clearDescendants(filtersDef, { ...s, [fid]: v }, fid));
    else onChange(fid, v);
  };
  const dirty = applyMode === "manual" && JSON.stringify(draft) !== valuesKey;

  const active = toActiveFilters(filtersDef, effValues);
  const hasActive = active.length > 0;
  const defById = Object.fromEntries(filtersDef.map((d) => [d.id, d]));

  const constraintsFor = (f) => {
    if (f.parent_id) {
      // Explicit chain: constrained only by its ancestors' selections
      return ancestors(f, defById)
        .filter((a) => a.dataset_id === f.dataset_id)
        .map((a) => activeFor(a, effValues)).filter(Boolean)
        .map(({ dataset_id, __scope, ...rest }) => rest);
    }
    // Automatic cascading: constrained by every other active filter on the same dataset
    return active.filter((a) => a.field !== f.field && a.dataset_id === f.dataset_id)
      .map(({ dataset_id, __scope, ...rest }) => rest);
  };

  const st = settings.style || {};
  const compact = !!st.compact;
  const barStyle = {};
  if (st.bg) barStyle.background = st.bg;
  if (st.radius != null && st.radius !== "") barStyle.borderRadius = Number(st.radius);
  if (st.border) { barStyle.border = `1px solid ${st.border}`; }

  return (
    <div data-testid="filter-bar" style={barStyle}
      className={vertical ? "flex flex-col items-stretch gap-3 px-1 py-2 [&>*]:w-full" : `flex flex-wrap items-center ${compact ? "gap-1.5 px-1 py-1" : "gap-2 px-1 py-2"}`}>
      {!vertical && <span className="flex items-center gap-1 text-xs text-[color:var(--ink-3)] mr-1"><FilterIcon className="w-3.5 h-3.5" />{t("filters")}</span>}
      {visibleDefs.map((f) => (
        <FilterControl
          key={f.id} f={f} value={effValues[f.id]}
          onChange={(v) => handleChange(f.id, v)}
          otherFilters={constraintsFor(f)}
          linked={!!f.parent_id}
          disabled={!!f.locked && !editable}
          missingRequired={!!f.required && !activeFor(f, effValues)}
          publicToken={publicToken} editable={editable} onRemove={() => onRemove && onRemove(f.id)}
          widgets={widgets} onUpdateDef={onUpdateDef}
        />
      ))}
      {applyMode === "manual" && (
        <span className="flex items-center gap-1">
          <button data-testid="filter-apply-button" onClick={() => onApplyAll && onApplyAll(draft)} disabled={!dirty}
            className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${dirty ? "bg-[color:var(--brand-ocean)] text-white hover:opacity-90" : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] cursor-default"}`}>
            <Check className="w-3.5 h-3.5" />{dirty ? t("apply") : t("applied")}
          </button>
          {dirty && (
            <button data-testid="filter-reset-button" onClick={() => setDraft(values)}
              className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--ink)] px-2 py-1.5 rounded-full hover:bg-[color:var(--surface-2)]">
              Reset
            </button>
          )}
        </span>
      )}
      {hasActive && onClearAll && (
        <button data-testid="filter-clear-all-button" onClick={onClearAll}
          className="flex items-center gap-1 text-xs text-[color:var(--brand-ocean)] hover:text-[color:var(--ink)] px-2.5 py-1.5 rounded-full hover:bg-[color:var(--surface-2)] transition-colors">
          <X className="w-3.5 h-3.5" />Clear all
        </button>
      )}
      {editable && onSettingsChange && <BarSettings settings={settings} onChange={onSettingsChange} />}
      {settings.show_summary && !vertical && (
        <FilterSummary filtersDef={filtersDef} values={values} onChange={onChange} />
      )}
    </div>
  );
}

// Item 195.17: active-filter summary chips, each individually removable.
function FilterSummary({ filtersDef, values, onChange }) {
  const chips = filtersDef.filter((f) => activeFor(f, values)).map((f) => {
    const v = values[f.id];
    let txt;
    if (Array.isArray(v)) txt = v.length > 2 ? `${v.slice(0, 2).join(", ")} +${v.length - 2}` : v.join(", ");
    else if (v && typeof v === "object") txt = `last ${v.n} days`;
    else txt = String(v);
    return { id: f.id, label: f.label || f.field, txt };
  });
  if (!chips.length) return null;
  return (
    <div data-testid="filter-summary" className="w-full flex flex-wrap items-center gap-1.5 pt-1">
      {chips.map((c) => (
        <span key={c.id} data-testid={`filter-summary-chip-${c.id}`} className="inline-flex items-center gap-1 text-[11px] bg-[color:var(--surface-2)] text-[color:var(--ink-2)] rounded-full pl-2.5 pr-1 py-0.5">
          <span className="text-[color:var(--ink-3)]">{c.label}:</span> {c.txt}
          <button onClick={() => onChange(c.id, null)} className="p-0.5 rounded-full hover:bg-[color:var(--surface)] text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
}

// Item 195: filter bar behaviour & appearance settings (author only, persisted on the dashboard).
function BarSettings({ settings, onChange }) {
  const st = settings.style || {};
  const setStyle = (patch) => onChange({ style: { ...st, ...patch } });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="filter-bar-settings-btn" title="Filter bar settings" className="p-1.5 rounded-full text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">
          <Settings2 className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3 space-y-2.5">
        <div className="text-xs font-semibold text-[color:var(--ink-2)]">Filter bar settings</div>
        <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">Pin while scrolling (viewer)
          <Checkbox data-testid="filter-bar-sticky" checked={!!settings.sticky} onCheckedChange={(v) => onChange({ sticky: !!v })} />
        </label>
        <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">Show active filter chips
          <Checkbox data-testid="filter-bar-summary" checked={!!settings.show_summary} onCheckedChange={(v) => onChange({ show_summary: !!v })} />
        </label>
        <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">Compact controls
          <Checkbox data-testid="filter-bar-compact" checked={!!st.compact} onCheckedChange={(v) => setStyle({ compact: !!v })} />
        </label>
        <div className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">Apply mode
          <Select value={settings.apply_mode === "manual" ? "manual" : "instant"} onValueChange={(v) => onChange({ apply_mode: v })}>
            <SelectTrigger data-testid="filter-bar-apply-mode" className="h-7 w-[120px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Instant</SelectItem>
              <SelectItem value="manual">Apply button</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">Background
          <span className="flex items-center gap-1">
            <input data-testid="filter-bar-bg" type="color" value={st.bg || "#ffffff"} onChange={(e) => setStyle({ bg: e.target.value })} className="w-7 h-7 rounded cursor-pointer border border-[color:var(--glass-border)]" />
            {st.bg && <button onClick={() => setStyle({ bg: "" })} className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">reset</button>}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">Corner radius
          <Input data-testid="filter-bar-radius" type="number" min={0} max={32} value={st.radius ?? ""} onChange={(e) => setStyle({ radius: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="0" className="h-7 w-[70px] text-xs bg-[color:var(--surface-2)]" />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterControl({ f, value, onChange, otherFilters, linked, disabled, missingRequired, publicToken, editable, onRemove, widgets = [], onUpdateDef }) {
  const [options, setOptions] = useState([]);
  const otherKey = JSON.stringify(otherFilters);

  useEffect(() => {
    if (["daterange", "numrange", "search", "toggle", "tier"].includes(f.type)) return;
    fetchDistinct({ datasetId: f.dataset_id, field: f.field, filters: otherFilters, publicToken })
      .then(setOptions).catch(() => setOptions([]));
    // otherFilters is tracked through its serialized form otherKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.dataset_id, f.field, f.type, otherKey, publicToken]);

  const label = f.label || f.field;
  const ring = missingRequired ? "ring-1 ring-[color:#f59e0b]" : "";

  return (
    <div className="flex items-center">
      {f.type === "multiselect" ? (
        <MultiSelect label={label} linked={linked} disabled={disabled} className={ring} options={options} value={value || []} onChange={onChange} testid={`gf-${f.field}`} />
      ) : f.type === "checkbox" ? (
        <MultiSelect label={label} linked={linked} disabled={disabled} className={ring} options={options}
          value={Array.isArray(value) ? value : (value?.v || [])}
          onChange={(arr) => onChange(Array.isArray(value) || !value?.exclude ? arr : { v: arr, exclude: true })} testid={`gf-${f.field}`} />
      ) : f.type === "search" ? (
        <div className={`flex items-center gap-1.5 glass-card px-3 py-1.5 ${ring}`}>
          <Search className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
          <Input data-testid={`gf-${f.id}-search`} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
            placeholder={f.placeholder || `Search ${label}`} className="h-7 w-[170px] border-0 bg-transparent text-sm p-0" />
          {value ? <button data-testid={`gf-${f.id}-search-clear`} onClick={() => onChange(null)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button> : null}
        </div>
      ) : f.type === "toggle" ? (
        <button data-testid={`gf-${f.id}-toggle`} disabled={disabled} onClick={() => onChange(!value)}
          className={`flex items-center gap-1.5 glass-card px-3 py-1.5 text-sm ${value ? "ring-1 ring-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-2)]"} ${ring}`}>
          <Check className={`w-3.5 h-3.5 ${value ? "opacity-100" : "opacity-30"}`} />{label}
        </button>
      ) : f.type === "tier" ? (
        <div className={`flex items-center gap-1.5 glass-card pl-3 pr-1 py-1 ${ring}`}>
          <span className="text-xs text-[color:var(--ink-3)]">{label}</span>
          <Select value={value ? String(value) : "__all__"} onValueChange={(v) => onChange(v === "__all__" ? null : v)} disabled={disabled}>
            <SelectTrigger data-testid={`gf-${f.id}-tier`} className="h-7 border-0 bg-transparent min-w-[110px] text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any</SelectItem>
              {(f.levels || []).map((l) => <SelectItem key={l.key} value={String(l.key)}>{l.label || l.key}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ) : f.type === "daterange" ? (
        <DateRange label={label} disabled={disabled} className={ring} value={value} onChange={onChange} testid={`gf-${f.field}`} />
      ) : f.type === "numrange" ? (
        <NumRange label={label} disabled={disabled} className={ring} value={value} onChange={onChange} testid={`gf-${f.field}`} />
      ) : (
        <div className={`flex items-center gap-1.5 glass-card pl-3 pr-1 py-1 ${ring}`}>
          {linked && <Link2 className="w-3 h-3 text-[color:var(--brand-mint)]" title="Linked to parent filter" />}
          {disabled && <Lock className="w-3 h-3 text-[color:var(--ink-3)]" title="Locked by the author" />}
          <span className="text-xs text-[color:var(--ink-3)]">{label}</span>
          <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? null : v)} disabled={disabled}>
            <SelectTrigger data-testid={`gf-${f.field}`} className="h-7 border-0 bg-transparent min-w-[100px] text-sm"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {missingRequired && <span data-testid="filter-required-hint" className="ml-1 text-[10px] text-[color:#f59e0b] font-medium">required</span>}
      {editable && onUpdateDef && <ScopeEditor f={f} widgets={widgets} onUpdateDef={onUpdateDef} />}
      {editable && <button onClick={onRemove} className="ml-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

// Item 195.9: per-filter widget scope — target the whole dashboard or specific widgets.
export function ScopeEditor({ f, widgets, onUpdateDef }) {
  const scope = f.scope || { mode: "all", widget_ids: [] };
  const scoped = scope.mode && scope.mode !== "all";
  const setScope = (patch) => onUpdateDef(f.id, { scope: { ...scope, ...patch } });
  const eligible = widgets.filter((w) => !["text", "image"].includes(w.type) && w.dataset_id === f.dataset_id);
  const toggle = (wid) => {
    const set = new Set(scope.widget_ids || []);
    set.has(wid) ? set.delete(wid) : set.add(wid);
    setScope({ widget_ids: [...set] });
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`filter-scope-btn-${f.field}`} title="Which widgets does this filter affect?"
          className={`ml-1 p-0.5 rounded ${scoped ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"}`}>
          <Target className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 space-y-2">
        <div className="text-xs font-semibold text-[color:var(--ink-2)]">Filter scope — {f.label || f.field}</div>
        <Select value={scope.mode || "all"} onValueChange={(v) => setScope({ mode: v })}>
          <SelectTrigger data-testid={`filter-scope-mode-${f.field}`} className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All widgets</SelectItem>
            <SelectItem value="include">Only selected widgets</SelectItem>
            <SelectItem value="exclude">All except selected</SelectItem>
          </SelectContent>
        </Select>
        {scoped && (
          <ScrollArea className="max-h-52">
            <div className="space-y-1 pr-2">
              {eligible.length === 0 && <div className="text-xs text-[color:var(--ink-3)] py-2 text-center">No widgets on this dataset</div>}
              {eligible.map((w) => (
                <label key={w.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-[color:var(--surface-2)] cursor-pointer">
                  <Checkbox checked={(scope.widget_ids || []).includes(w.id)} onCheckedChange={() => toggle(w.id)} />
                  <span className="text-xs text-[color:var(--ink-2)] truncate">{w.title || w.type}</span>
                </label>
              ))}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}

function MultiSelect({ label, linked, disabled, className = "", options, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const toggle = (o) => { const set = new Set(value); set.has(o) ? set.delete(o) : set.add(o); onChange([...set]); };
  const shown = q ? options.filter((o) => String(o).toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testid} disabled={disabled} className={`flex items-center gap-1.5 glass-card px-3 py-1.5 text-sm ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`}>
          {linked && <Link2 className="w-3 h-3 text-[color:var(--brand-mint)]" />}
          {disabled && <Lock className="w-3 h-3 text-[color:var(--ink-3)]" />}
          <span className="text-xs text-[color:var(--ink-3)]">{label}</span>
          <span className="text-[color:var(--ink-2)]">{value.length ? `${value.length} selected` : "All"}</span>
          <ChevronDown className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--glass-border)]">
          <span className="text-xs font-medium text-[color:var(--ink-2)]">{label}</span>
          {value.length > 0 && <button onClick={() => onChange([])} className="text-xs text-[color:var(--brand-ocean)]">Clear</button>}
        </div>
        {options.length > 8 && (
          <div className="px-2 pt-2 relative">
            <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 mt-1 -translate-y-1/2 text-[color:var(--ink-3)]" />
            <Input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-7 pl-7 text-xs bg-[color:var(--surface-2)]" />
          </div>
        )}
        <ScrollArea className="max-h-60">
          <div className="p-2 space-y-1">
            {shown.length === 0 && <div className="text-xs text-[color:var(--ink-3)] px-2 py-3 text-center">No options</div>}
            {shown.map((o) => (
              <label key={o} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[color:var(--surface-2)] cursor-pointer">
                <Checkbox checked={value.includes(o)} onCheckedChange={() => toggle(o)} />
                <span className="text-sm text-[color:var(--ink-2)] truncate">{o}</span>
              </label>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function NumRange({ label, disabled, className = "", value, onChange, testid }) {
  const v = Array.isArray(value) ? value : [null, null];
  const set = (i, raw) => {
    const next = [...v];
    next[i] = raw === "" ? null : Number(raw);
    onChange(next[0] == null && next[1] == null ? null : next);
  };
  return (
    <div className={`flex items-center gap-1.5 glass-card px-3 py-1.5 ${disabled ? "opacity-60" : ""} ${className}`}>
      <Hash className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
      {disabled && <Lock className="w-3 h-3 text-[color:var(--ink-3)]" />}
      <span className="text-xs text-[color:var(--ink-3)]">{label}</span>
      <Input data-testid={`${testid}-min`} type="number" disabled={disabled} value={v[0] ?? ""} onChange={(e) => set(0, e.target.value)} placeholder="min" className="h-7 w-[76px] border-0 bg-transparent text-sm p-0" />
      <span className="text-[color:var(--ink-3)] text-xs">–</span>
      <Input data-testid={`${testid}-max`} type="number" disabled={disabled} value={v[1] ?? ""} onChange={(e) => set(1, e.target.value)} placeholder="max" className="h-7 w-[76px] border-0 bg-transparent text-sm p-0" />
    </div>
  );
}

function DateRange({ label, disabled, className = "", value, onChange, testid }) {
  const isLastN = value && typeof value === "object" && !Array.isArray(value) && value.preset === "last_n";
  const preset = isLastN ? "last_n" : typeof value === "string" ? value : Array.isArray(value) ? "custom" : "__none__";
  const custom = Array.isArray(value) ? value : ["", ""];
  const pick = (v) => {
    if (v === "__none__") onChange(null);
    else if (v === "custom") onChange(["", ""]);
    else if (v === "last_n") onChange({ preset: "last_n", n: 14 });
    else onChange(v);
  };
  return (
    <div className={`flex items-center gap-1.5 glass-card px-3 py-1.5 ${disabled ? "opacity-60" : ""} ${className}`}>
      <Calendar className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
      {disabled && <Lock className="w-3 h-3 text-[color:var(--ink-3)]" />}
      <span className="text-xs text-[color:var(--ink-3)]">{label}</span>
      <Select value={preset} onValueChange={pick} disabled={disabled}>
        <SelectTrigger data-testid={`${testid}-preset`} className="h-7 border-0 bg-transparent min-w-[110px] text-sm"><SelectValue placeholder="All time" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">All time</SelectItem>
          {DATE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {isLastN && (
        <Input data-testid={`${testid}-lastn`} type="number" min={1} disabled={disabled} value={value.n ?? ""} onChange={(e) => onChange({ preset: "last_n", n: e.target.value === "" ? "" : Number(e.target.value) })}
          className="h-7 w-[64px] border-0 bg-transparent text-sm p-0" placeholder="days" />
      )}
      {preset === "custom" && (
        <>
          <Input data-testid={`${testid}-from`} type="date" disabled={disabled} value={custom[0] || ""} onChange={(e) => onChange([e.target.value, custom[1]])} className="h-7 w-[130px] border-0 bg-transparent text-sm p-0" />
          <span className="text-[color:var(--ink-3)] text-xs">to</span>
          <Input data-testid={`${testid}-to`} type="date" disabled={disabled} value={custom[1] || ""} onChange={(e) => onChange([custom[0], e.target.value])} className="h-7 w-[130px] border-0 bg-transparent text-sm p-0" />
        </>
      )}
    </div>
  );
}
